// The albums page's data hook: lists the Owner's Albums via `GET /api/albums`
// (ADR-0059) and manages them — create (`POST /api/albums`), rename
// (`POST /api/albums/:id/rename`), and delete (`DELETE /api/albums/:id`),
// reflecting each change in place. Thin over the `api` client (which does the
// request shaping + error normalization); this only sequences the calls + holds
// React state, so it is unit-testable with a stub client (like `useDevices`).
//
// Mutations re-list from the server (the source of truth for itemCount/cover/
// updatedAt) rather than patching the row locally — an Album's derived fields move
// with its items, so a re-list keeps the summaries honest. Per-mutation in-flight
// flags disable just the affected control.

import { useCallback, useEffect, useRef, useState } from "react";
import { type ApiClient, type AlbumSummary, ApiError } from "./api";

export interface UseAlbumsState {
  albums: AlbumSummary[];
  /** True during the first list load (drives the initial spinner). */
  loading: boolean;
  /** A non-auth error message to surface (auth → `unauthorized`). */
  error: string | null;
  /** The Owner Session is missing/expired — show the re-auth affordance. */
  unauthorized: boolean;
  /** A create/rename/delete is in flight — disable the mutating controls. */
  busy: boolean;
  /** Create an Album; on success the list re-lists. Returns the new id (or null on failure). */
  create: (name: string) => Promise<string | null>;
  /** Rename an Album; on success the list re-lists. Returns whether it succeeded. */
  rename: (id: string, name: string) => Promise<boolean>;
  /** Delete an Album; on success the list re-lists. Returns whether it succeeded. */
  remove: (id: string) => Promise<boolean>;
  /** Reload the list from scratch. */
  reload: () => void;
}

/** Manage the Owner's Album list + create/rename/delete for the albums page. */
export function useAlbums(client: ApiClient): UseAlbumsState {
  const [albums, setAlbums] = useState<AlbumSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [busy, setBusy] = useState(false);

  // Invalidate an in-flight list when a reload supersedes it (stale-write guard).
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const list = await client.listAlbums();
      if (id !== reqId.current) return;
      setAlbums(list);
    } catch (err) {
      if (id !== reqId.current) return;
      if (err instanceof ApiError && err.isUnauthorized) {
        setUnauthorized(true);
      } else {
        setError(err instanceof Error ? err.message : "failed to load albums");
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [client]);

  // Run one mutation, then re-list so the derived summaries (itemCount/cover/updatedAt)
  // stay honest. A shared wrapper for create/rename/delete: it sets busy, catches auth vs
  // generic errors, and never leaves `busy` stuck. Returns whether it succeeded.
  const mutate = useCallback(
    async (fn: () => Promise<unknown>, fallback: string): Promise<boolean> => {
      if (busy) return false;
      setBusy(true);
      setError(null);
      try {
        await fn();
        await load();
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized) {
          setUnauthorized(true);
        } else {
          setError(err instanceof Error ? err.message : fallback);
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, load],
  );

  const create = useCallback(
    async (name: string): Promise<string | null> => {
      let newId: string | null = null;
      await mutate(async () => {
        const { id } = await client.createAlbum(name);
        newId = id;
      }, "failed to create album");
      return newId;
    },
    [client, mutate],
  );

  const rename = useCallback(
    async (id: string, name: string): Promise<boolean> =>
      mutate(() => client.renameAlbum(id, name), "failed to rename album"),
    [client, mutate],
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> =>
      mutate(() => client.deleteAlbum(id), "failed to delete album"),
    [client, mutate],
  );

  const reload = useCallback(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return { albums, loading, error, unauthorized, busy, create, rename, remove, reload };
}
